import { ArrowLeft, ArrowRight, Check, Circle, Clock3, RotateCcw, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { Button } from '@/shared/ui/button';

import { fieldNextStep } from '../field-next-step';
import {
  fieldProgressSourceDestination,
  fieldProgressStageDestination,
} from '../field-progress-navigation';

import { FieldTime } from './field-primitives';
import { FieldProgressPath } from './field-progress-path';

import type { FieldDestination } from '../field-overview';
import type {
  FieldActorId,
  FieldProgress,
  FieldProgressCell,
  FieldProgressEvidence,
  FieldProgressStage,
  FieldProgressState,
  FieldSnapshot,
} from '@mio/shared';

import './field-project-map.css';

const fieldProgressStageLabels: Record<FieldProgressStage, string> = {
  agreed: 'Agreed',
  designed: 'Designed',
  built: 'Built',
  verified: 'Verified',
  controls: 'Controls',
  shipped: 'Shipped',
};
const stateLabels: Record<FieldProgressState, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  waiting: 'Waiting',
  verified: 'Verified',
  needs_recheck: 'Needs recheck',
};
const stages = Object.keys(fieldProgressStageLabels) as FieldProgressStage[];
const stateIcons = {
  not_started: Circle,
  in_progress: Circle,
  waiting: Clock3,
  verified: Check,
  needs_recheck: RotateCcw,
};
/** A refused read receipt retries on later snapshots, but never loops while the server keeps refusing. */
const VISIT_ATTEMPTS = 3;

interface ProjectMapProps {
  snapshot: FieldSnapshot;
  unreadByActor: Partial<Record<FieldActorId, number>>;
  onNavigate: (destination: FieldDestination) => void;
  /** Marks the received changes as seen. Resolves false when the receipt was not recorded. */
  onVisit: (seenRevision: number) => Promise<boolean>;
  busy: boolean;
  visible?: boolean;
}

function hasUnseenChanges(progress: FieldProgress) {
  return progress.changes.some(
    (change) => progress.lastSeenRevision === null || change.revision > progress.lastSeenRevision,
  );
}

function EvidenceList({
  evidence,
  snapshot,
  onNavigate,
}: {
  evidence: FieldProgressEvidence[];
  snapshot: FieldSnapshot;
  onNavigate: ProjectMapProps['onNavigate'];
}) {
  if (!evidence.length)
    return <p className="field-map-muted">No supporting evidence recorded yet.</p>;
  return (
    <ul className="field-map-evidence">
      {evidence.map((item, index) => (
        <li key={`${item.kind}:${item.id}:${index}`}>
          <button
            type="button"
            onClick={() => onNavigate(fieldProgressSourceDestination(snapshot, item))}
          >
            <span>{item.label}</span>
            <ArrowRight size={14} aria-hidden="true" />
          </button>
          <p>{item.detail}</p>
          {item.stale || item.revision || item.at ? (
            <div className="field-map-evidence-meta">
              {item.stale ? <strong>Previous evidence · needs recheck</strong> : null}
              {item.revision ? (
                <code title={item.revision}>{item.revision.slice(0, 8)}</code>
              ) : null}
              {item.at ? <FieldTime value={item.at} /> : null}
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function CellDetail({
  cell,
  rowTitle,
  snapshot,
  onNavigate,
  onClose,
}: {
  cell: FieldProgressCell;
  rowTitle: string;
  snapshot: FieldSnapshot;
  onNavigate: ProjectMapProps['onNavigate'];
  onClose: () => void;
}) {
  const id = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
    heading.current?.scrollIntoView?.({ block: 'nearest' });
  }, []);
  const actor = cell.waitingOn[0];
  const actorName = snapshot.actors.find((item) => item.id === actor)?.name.split(' ')[0];
  return (
    <section className="field-map-detail" aria-labelledby={id}>
      <div className="field-map-detail-heading">
        <div>
          <p className="field-map-eyebrow">{rowTitle}</p>
          <h3 ref={heading} tabIndex={-1} id={id}>
            {fieldProgressStageLabels[cell.stage]}{' '}
            {fieldProgressStageLabels[cell.stage] !== stateLabels[cell.state] ? (
              <span data-state={cell.state}>{stateLabels[cell.state]}</span>
            ) : null}
          </h3>
        </div>
        <button
          className="field-map-close"
          type="button"
          aria-label="Close cell details"
          onClick={onClose}
        >
          <X size={17} aria-hidden="true" />
        </button>
      </div>
      <p className="field-map-summary">{cell.summary}</p>
      <div className="field-map-detail-columns">
        <div>
          <h4>What this needs to establish</h4>
          <p>{cell.standard}</p>
        </div>
        <div>
          <h4>Next step</h4>
          <p>{cell.nextStep}</p>
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              onNavigate(
                actor
                  ? {
                      section: 'inbox',
                      actorId: actor,
                      draft: `Can we review ${rowTitle.toLowerCase()} — ${fieldProgressStageLabels[cell.stage].toLowerCase()}? ${cell.nextStep}`,
                      sourceIds: cell.evidence.map((item) => item.id).slice(0, 20),
                    }
                  : fieldProgressStageDestination[cell.stage],
              )
            }
          >
            {actor ? `Discuss with ${actorName ?? actor}` : 'Open this work'}
            <ArrowRight size={14} aria-hidden="true" />
          </Button>
        </div>
      </div>
      <h4>Evidence behind this state</h4>
      <EvidenceList evidence={cell.evidence} snapshot={snapshot} onNavigate={onNavigate} />
    </section>
  );
}

export function FieldProjectMap({
  snapshot,
  unreadByActor,
  onNavigate,
  onVisit,
  busy,
  visible = true,
}: ProjectMapProps) {
  const progress = snapshot.progress;
  const [detailed, setDetailed] = useState(false);
  const [selectedCell, setSelectedCell] = useState<string | null>(null);
  const [changesOpen, setChangesOpen] = useState(false);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [highlighted, setHighlighted] = useState<string[]>([]);
  const [baseline, setBaseline] = useState<number | null | undefined>(undefined);
  const [tabVisible, setTabVisible] = useState(() => document.visibilityState !== 'hidden');
  useEffect(() => {
    const update = () => setTabVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  const visiting = useRef(false);
  const attempts = useRef(0);
  const inFlight = useRef(false);
  const latestVisit = useRef(onVisit);
  latestVisit.current = onVisit;
  const mapId = useId();
  const cellButtons = useRef(new Map<string, HTMLButtonElement>());
  const cellDetails = useRef<HTMLDivElement>(null);
  const next = fieldNextStep(snapshot, unreadByActor);
  useEffect(() => {
    if (!visible || !tabVisible) {
      visiting.current = false;
      attempts.current = 0;
      return;
    }
    if (!progress) return;
    if (!visiting.current) {
      visiting.current = true;
      setBaseline(progress.lastSeenRevision);
    }
    // Only unseen changes need a receipt; an unchanged view writes nothing.
    if (
      !hasUnseenChanges(progress) ||
      inFlight.current ||
      attempts.current >= VISIT_ATTEMPTS ||
      busy ||
      snapshot.engagement.status !== 'active'
    )
      return;
    attempts.current += 1;
    inFlight.current = true;
    void latestVisit.current(progress.snapshotRevision).finally(() => {
      inFlight.current = false;
    });
  }, [visible, tabVisible, progress, busy, snapshot.engagement.status]);
  if (!progress)
    return (
      <div className="field-project-map">
        <h2>Progress</h2>
        <p className="field-map-muted">
          Progress is not available in this snapshot yet. Your brief and work remain available.
        </p>
        <Button variant="secondary" onClick={() => onNavigate({ section: 'engagement' })}>
          Open project brief
        </Button>
      </div>
    );
  const row = progress.rows.find((item) => item.cells.some((cell) => cell.id === selectedCell));
  const cell = row?.cells.find((item) => item.id === selectedCell);
  const visitBaseline = baseline === undefined ? progress.lastSeenRevision : baseline;
  const changes = progress.changes.filter(
    (change) => visitBaseline === null || change.revision > visitBaseline,
  );
  const verifiedCount = progress.rows.filter(
    (item) =>
      item.id !== 'handoff' &&
      item.cells.some((entry) => entry.stage === 'verified' && entry.state === 'verified'),
  ).length;
  const verifiedRequirements = progress.requirements.filter(
    (item) => item.state === 'verified',
  ).length;
  const completed = snapshot.assessment.status === 'completed';
  const headline = completed
    ? 'Final submission accepted'
    : progress.readiness.ready
      ? 'Ready to request final review'
      : 'Not ready for final review';
  const chooseCell = (id: string) => {
    setSelectedCell(id);
    setHighlighted([]);
  };
  const seeAffected = (ids: string[]) => {
    setHighlighted(ids);
    setSelectedCell(ids[0] ?? null);
    setChangesOpen(false);
    requestAnimationFrame(() => {
      const heading = cellDetails.current?.querySelector('h3');
      heading?.focus({ preventScroll: true });
      heading?.scrollIntoView?.({ block: 'nearest' });
    });
  };
  if (!detailed)
    return (
      <FieldProgressPath
        snapshot={snapshot}
        unreadByActor={unreadByActor}
        onNavigate={onNavigate}
        onShowDetails={() => setDetailed(true)}
        onShowStageDetails={(stage) => {
          setDetailed(true);
          const stageCells = progress.rows.flatMap((item) =>
            item.id === 'handoff'
              ? stage === 'shipped'
                ? item.cells
                : []
              : item.cells.filter((entry) => entry.stage === stage),
          );
          const relevant =
            stageCells.find((entry) => entry.state === 'needs_recheck') ??
            stageCells.find((entry) => entry.state !== 'verified') ??
            stageCells[0];
          if (relevant) seeAffected([relevant.id]);
        }}
      />
    );
  return (
    <div className="field-project-map" aria-label="Progress">
      <Button
        className="field-map-back"
        size="sm"
        variant="ghost"
        onClick={() => setDetailed(false)}
      >
        <ArrowLeft size={14} aria-hidden="true" />
        Back to progress
      </Button>
      <header className="field-map-header">
        <div>
          <p className="field-map-eyebrow">Your delivery</p>
          <h2>Progress</h2>
          <p>
            {completed
              ? 'Your submission is accepted. Its evidence and instructor report remain available.'
              : progress.framing === 'remaining'
                ? 'What still needs evidence before you deliver.'
                : progress.framing === 'checkpoint'
                  ? 'Review what is established and what needs to change next.'
                  : 'See what works, what needs checking, and what to do next.'}
          </p>
        </div>
        <Button size="sm" variant="secondary" onClick={() => onNavigate({ section: 'engagement' })}>
          Project brief
        </Button>
      </header>
      <div className="field-map-status">
        <button
          type="button"
          aria-expanded={readinessOpen}
          aria-controls={`${mapId}-readiness`}
          onClick={() => setReadinessOpen(!readinessOpen)}
        >
          <span data-ready={progress.readiness.ready}>{headline}</span>
          <span>
            {progress.readiness.blockerCount}{' '}
            {progress.readiness.blockerCount === 1 ? 'blocker' : 'blockers'}
          </span>
        </button>
        <button
          type="button"
          aria-expanded={changesOpen}
          aria-controls={`${mapId}-changes`}
          onClick={() => setChangesOpen(!changesOpen)}
        >
          {visitBaseline === null ? 'Recent changes' : 'Since your last visit'}{' '}
          <strong>{changes.length}</strong>
        </button>
      </div>
      {readinessOpen ? (
        <section
          id={`${mapId}-readiness`}
          className="field-map-readiness"
          aria-label="Final review readiness"
        >
          <h3>What prevents final review</h3>
          {progress.readiness.blockers.length ? (
            <ul>
              {progress.readiness.blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          ) : (
            <p>
              The evidence gates are satisfied. Final acceptance still requires instructor review.
            </p>
          )}
          <details>
            <summary>All readiness gates</summary>
            <ul>
              {progress.readiness.gates.map((gate) => (
                <li key={gate.id}>
                  <strong>{gate.passed ? 'Satisfied' : 'Needs evidence'}</strong> · {gate.label}
                </li>
              ))}
            </ul>
          </details>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onNavigate({ section: 'assessment' })}
          >
            Open formal readiness review
          </Button>
        </section>
      ) : null}
      {changesOpen ? (
        <section
          id={`${mapId}-changes`}
          className="field-map-changes"
          aria-label="Changes since last visit"
        >
          <h3>{visitBaseline === null ? 'Recent changes' : 'Since your last visit'}</h3>
          {changes.length ? (
            <ol>
              {changes.map((change) => (
                <li key={change.id}>
                  <div>
                    <strong>{change.title}</strong>
                    <p>{change.detail}</p>
                    <FieldTime value={change.at} />
                  </div>
                  {change.cellIds.length ? (
                    <Button variant="ghost" size="sm" onClick={() => seeAffected(change.cellIds)}>
                      See affected work
                      <ArrowRight size={14} aria-hidden="true" />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ol>
          ) : (
            <p>No new evidence changes since you last opened Progress.</p>
          )}
          {progress.earlierChangesOmitted ? (
            <p className="field-map-muted">
              Only the most recent evidence changes are shown. Earlier work remains available
              through its source records.
            </p>
          ) : null}
        </section>
      ) : null}
      <section className="field-map-next" aria-label="Suggested next step">
        <div>
          <p className="field-map-eyebrow">Next</p>
          <h3>{next.title}</h3>
          <p>{next.reason}</p>
        </div>
        <Button size="sm" variant="secondary" onClick={() => onNavigate(next.destination)}>
          {next.label}
          <ArrowRight size={14} aria-hidden="true" />
        </Button>
      </section>
      <div className="field-map-table-wrap">
        <table className="field-map-table">
          <caption>
            <span>
              {verifiedCount
                ? `${verifiedCount} client ${verifiedCount === 1 ? 'outcome has' : 'outcomes have'} passing checks on this submission`
                : 'Your client outcomes and delivery work'}
            </span>
            <span>Select any cell to inspect its evidence.</span>
          </caption>
          <thead>
            <tr>
              <th scope="col">Outcome</th>
              {stages.map((stage) => (
                <th key={stage} scope="col">
                  {fieldProgressStageLabels[stage]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {progress.rows.map((item) => (
              <tr key={item.id}>
                <th scope="row">
                  <strong>{item.title}</strong>
                  <span>{item.description}</span>
                </th>
                {stages.map((stage) => {
                  const entry = item.cells.find((entry) => entry.stage === stage);
                  if (!entry) return <td key={stage}>—</td>;
                  const Icon = stateIcons[entry.state];
                  const names = entry.waitingOn
                    .map(
                      (actor) =>
                        snapshot.actors.find((person) => person.id === actor)?.name.split(' ')[0] ??
                        actor,
                    )
                    .join(', ');
                  return (
                    <td key={stage}>
                      <button
                        type="button"
                        ref={(node) => {
                          if (node) cellButtons.current.set(entry.id, node);
                          else cellButtons.current.delete(entry.id);
                        }}
                        data-state={entry.state}
                        data-selected={selectedCell === entry.id || undefined}
                        data-affected={highlighted.includes(entry.id) || undefined}
                        aria-label={`${item.title}, ${fieldProgressStageLabels[stage]}: ${stateLabels[entry.state]}${names ? `, waiting on ${names}` : ''}`}
                        aria-expanded={selectedCell === entry.id}
                        aria-controls={selectedCell === entry.id ? `${mapId}-detail` : undefined}
                        onClick={() => chooseCell(entry.id)}
                      >
                        <span className="field-map-mobile-stage">
                          {fieldProgressStageLabels[stage]}
                        </span>
                        <Icon size={17} aria-hidden="true" />
                        <span>
                          {entry.state === 'waiting' && names ? names : stateLabels[entry.state]}
                        </span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="field-map-legend" aria-label="Progress states">
        {(Object.keys(stateLabels) as FieldProgressState[]).map((state) => {
          const Icon = stateIcons[state];
          return (
            <span key={state} data-state={state}>
              <Icon size={12} aria-hidden="true" />
              {stateLabels[state]}
            </span>
          );
        })}
      </div>
      {cell && row ? (
        <div ref={cellDetails} id={`${mapId}-detail`}>
          <CellDetail
            key={cell.id}
            cell={cell}
            rowTitle={row.title}
            snapshot={snapshot}
            onNavigate={onNavigate}
            onClose={() => {
              setSelectedCell(null);
              setHighlighted([]);
              cellButtons.current.get(cell.id)?.focus();
            }}
          />
        </div>
      ) : null}
      <div className="field-map-support">
        <details className="field-map-disclosure">
          <summary>
            <span>Waiting on others</span>
            <span>{progress.waitingOn.length} pending</span>
          </summary>
          <div className="field-map-disclosure-body">
            {progress.waitingOn.length ? (
              <ul className="field-map-work-list">
                {progress.waitingOn.map((item) => (
                  <li key={item.id}>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.detail}</p>
                      <span className="field-map-muted">
                        {item.actorId
                          ? (snapshot.actors.find((actor) => actor.id === item.actorId)?.name ??
                            item.actorId)
                          : 'Verification service'}{' '}
                        · since <FieldTime value={item.since} />
                      </span>
                    </div>
                    {item.cellIds.length ? (
                      <Button variant="ghost" size="sm" onClick={() => seeAffected(item.cellIds)}>
                        See work
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No pending owner confirmations or reviews.</p>
            )}
          </div>
        </details>
        <details className="field-map-disclosure">
          <summary>
            <span>Known scope</span>
            <span>
              {progress.requirements.length} known{' '}
              {progress.requirements.length === 1 ? 'requirement' : 'requirements'}
            </span>
          </summary>
          <div className="field-map-disclosure-body">
            <p className="field-map-muted">
              Confirmed requirements expand the known scope. A requirement counts as verified only
              on current review and trusted behaviour evidence; this is not code coverage.
            </p>
            {progress.requirements.length ? (
              <>
                <p className="field-map-scope-summary">
                  <strong>
                    {verifiedRequirements} of {progress.requirements.length} verified
                  </strong>{' '}
                  on the current candidate
                </p>
                <ul className="field-map-requirements">
                  {progress.requirements.map((item) => (
                    <li key={`${item.id}:${item.version}`}>
                      <details>
                        <summary>
                          <span>
                            {item.title} <small>v{item.version}</small>
                          </span>
                          <span data-state={item.state}>{stateLabels[item.state]}</span>
                        </summary>
                        <p>{item.nextStep}</p>
                        <p className="field-map-muted">
                          Owner{' '}
                          {snapshot.actors.find((actor) => actor.id === item.owner)?.name ??
                            item.owner}{' '}
                          · Last confirmed <FieldTime value={item.confirmedAt} />
                        </p>
                        {item.acceptance.length ? (
                          <ul className="field-map-acceptance">
                            {item.acceptance.map((example) => (
                              <li key={example}>{example}</li>
                            ))}
                          </ul>
                        ) : null}
                        {item.history.length ? (
                          <details className="field-map-history">
                            <summary>Requirement versions</summary>
                            <ul>
                              {item.history.map((version, index) => (
                                <li key={`${version.version}-${index}`}>
                                  Version {version.version} · {version.status} ·{' '}
                                  <FieldTime value={version.at} />
                                </li>
                              ))}
                            </ul>
                          </details>
                        ) : null}
                        {item.checkFamilies.length ? (
                          <p className="field-map-muted">
                            Check families: {item.checkFamilies.join(', ')}
                          </p>
                        ) : null}
                        <EvidenceList
                          snapshot={snapshot}
                          evidence={item.evidence}
                          onNavigate={onNavigate}
                        />
                      </details>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="field-map-muted">
                As owners confirm requirements, they appear here with their verification state.
              </p>
            )}
          </div>
        </details>
      </div>
    </div>
  );
}
